import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * Club Branding Context
 * Provides branding information based on the current club context
 */

// Default branding configurations for known clubs
const CLUB_BRANDING = {
  'all-american-dog-club': {
    siteName: 'All American',
    siteNameSecondary: 'Dog Club',
    isWorldPets: false,
    isAADC: true,
    primaryColor: '#c9762b',    // Warm brown (matches --brand-primary)
    secondaryColor: '#a65d1f',  // Darker brown (matches --brand-secondary)
    accentColor: '#e3c992',     // Golden tan (matches --brand-accent)
    logo: 'dog',
    tagline: 'Every Dog Is a Champion'
  },
  'worldpets': {
    siteName: 'WorldPets',
    siteNameSecondary: 'Global',
    isWorldPets: true,
    isAADC: false,
    primaryColor: '#059669',
    secondaryColor: '#0891b2',
    accentColor: '#ffffff',
    logo: 'globe',
    tagline: 'Connecting Pet Lovers Worldwide'
  },
  'club-canino-espana': {
    siteName: 'Club Canino',
    siteNameSecondary: 'de España',
    isWorldPets: false,
    isAADC: false,
    primaryColor: '#AA151B',
    secondaryColor: '#F1BF00',
    accentColor: '#ffffff',
    logo: 'dog',
    tagline: 'Cada Perro es un Campeón'
  }
};

// Default AADC branding (fallback - use AADC since this is the AADC app)
const DEFAULT_BRANDING = CLUB_BRANDING['all-american-dog-club'];

const ClubBrandingContext = createContext(null);

export const ClubBrandingProvider = ({ children, clubSlug, clubData }) => {
  const [branding, setBranding] = useState(() => {
    // If we have a known club slug, use its branding
    if (clubSlug && CLUB_BRANDING[clubSlug]) {
      return CLUB_BRANDING[clubSlug];
    }
    
    // If we have club data from API, use it to build branding
    if (clubData) {
      return {
        siteName: clubData.name?.split(' ').slice(0, -1).join(' ') || clubData.name,
        siteNameSecondary: clubData.name?.split(' ').slice(-1)[0] || '',
        isWorldPets: clubData.slug === 'worldpets',
        isAADC: clubData.slug === 'all-american-dog-club',
        primaryColor: clubData.primary_color || '#059669',
        secondaryColor: clubData.secondary_color || '#0891b2',
        accentColor: clubData.accent_color || '#ffffff',
        logo: clubData.pet_types?.includes('dog') ? 'dog' : 'globe',
        tagline: clubData.tagline || ''
      };
    }
    
    // Default to WorldPets branding
    return DEFAULT_BRANDING;
  });

  // Update branding when club context changes
  useEffect(() => {
    if (clubSlug && CLUB_BRANDING[clubSlug]) {
      setBranding(CLUB_BRANDING[clubSlug]);
    } else if (clubData) {
      setBranding({
        siteName: clubData.name?.split(' ').slice(0, -1).join(' ') || clubData.name,
        siteNameSecondary: clubData.name?.split(' ').slice(-1)[0] || '',
        isWorldPets: clubData.slug === 'worldpets',
        isAADC: clubData.slug === 'all-american-dog-club',
        primaryColor: clubData.primary_color || '#059669',
        secondaryColor: clubData.secondary_color || '#0891b2',
        accentColor: clubData.accent_color || '#ffffff',
        logo: clubData.pet_types?.includes('dog') ? 'dog' : 'globe',
        tagline: clubData.tagline || ''
      });
    } else {
      setBranding(DEFAULT_BRANDING);
    }
  }, [clubSlug, clubData]);

  // Apply branding colors to CSS variables so all components using
  // bg-primary, text-secondary, etc. reflect the club's colors
  useEffect(() => {
    if (branding.primaryColor) {
      const primaryHSL = hexToHSL(branding.primaryColor);
      const secondaryHSL = hexToHSL(branding.secondaryColor || branding.primaryColor);
      const accentHSL = hexToHSL(branding.accentColor || '#ffffff');
      
      document.documentElement.style.setProperty('--brand-primary', primaryHSL);
      document.documentElement.style.setProperty('--brand-secondary', secondaryHSL);
      document.documentElement.style.setProperty('--brand-accent', accentHSL);
    }
    
    // Cleanup: reset to defaults when unmounting or changing clubs
    return () => {
      // Only reset if we're actually unmounting the provider
      // (not just re-rendering with new values)
    };
  }, [branding.primaryColor, branding.secondaryColor, branding.accentColor]);

  return (
    <ClubBrandingContext.Provider value={branding}>
      {children}
    </ClubBrandingContext.Provider>
  );
};

/**
 * Convert hex color to HSL string (without "hsl()" wrapper)
 * Returns format: "H S% L%" for CSS variable usage
 */
function hexToHSL(hex) {
  if (!hex) return '25 75% 45%'; // Default AADC brown
  
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  // Convert to degrees and percentages
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  
  return `${h} ${s}% ${l}%`;
}

/**
 * Hook to access club branding
 * Returns branding object with siteName, colors, logo type, etc.
 */
export const useClubBranding = () => {
  const context = useContext(ClubBrandingContext);
  
  // If used outside provider, fall back to domain-based detection
  if (!context) {
    const hostname = window.location.hostname;
    
    // Check domain for branding
    if (hostname === 'worldpets.club' || hostname === 'www.worldpets.club') {
      return CLUB_BRANDING['worldpets'];
    }
    
    if (
      hostname === 'allamericandog.club' ||
      hostname === 'www.allamericandog.club' ||
      hostname === 'all-american.pet' ||
      hostname === 'www.all-american.pet'
    ) {
      return CLUB_BRANDING['all-american-dog-club'];
    }
    
    // Check URL path for club context
    const pathname = window.location.pathname;
    if (pathname.includes('/club/all-american-dog-club')) {
      return CLUB_BRANDING['all-american-dog-club'];
    }
    if (pathname.includes('/club/worldpets')) {
      return CLUB_BRANDING['worldpets'];
    }
    if (pathname.includes('/club/club-canino-espana')) {
      return CLUB_BRANDING['club-canino-espana'];
    }
    
    // Default to WorldPets
    return DEFAULT_BRANDING;
  }
  
  return context;
};

// Export branding configs for direct access if needed
export { CLUB_BRANDING, DEFAULT_BRANDING };

export default ClubBrandingContext;
